import React, {ReactNode} from 'react'
import {FormattedTime} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {RefundType} from '@vio/core/src/offer/business/cancellationPenalties'

interface BasePenaltyProps {
  refundType?: RefundType
  isExpired?: boolean
  isAuthenticateLater?: boolean | null
  hasChargeLaterRefundPolicy?: boolean
}

interface PenaltyContainerProps extends BasePenaltyProps {
  date: ReactNode
  timezone: string
  refundAmount: ReactNode
  amount: ReactNode
  id: string | number
  nonRefundable?: boolean
  dateTime: string
}

interface RefundInfoProps extends BasePenaltyProps {
  refundAmount: ReactNode
}

interface RefundLabelProps extends BasePenaltyProps {
  nonRefundable?: boolean
  amount?: ReactNode
}

interface PenaltyDateTimeProps {
  date: ReactNode
  dateTime: string
  timezone: string
}

const CapitalizedText = styled(Text)`
  &:first-letter {
    text-transform: capitalize;
  }
`

const StyledText = styled(Text)`
  text-decoration: none;
  &.--has-strike-through {
    text-decoration: line-through;
  }
`

const StyledPriceWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  &.--has-strike-through,
  &.--has-strike-through * {
    text-decoration: line-through;
  }
`

const getFreeCancellationMessage = (
  hasChargeLaterRefundPolicy?: boolean,
  isAuthenticateLater?: boolean | null
) => {
  if (hasChargeLaterRefundPolicy)
    if (isAuthenticateLater) {
      return (
        <FormattedMessageWrapper
          id="cancellationPenalties.fullRefundCompletedPayment"
          defaultMessage="Full refund of completed payment"
        />
      )
    } else {
      return (
        <FormattedMessageWrapper
          id="cancellationPenalties.fullRefundScheduledPayment"
          defaultMessage="Full refund of scheduled payment"
        />
      )
    }

  return (
    <FormattedMessageWrapper
      id="cancellationPenalties.fullRefund"
      defaultMessage="Full refund"
    />
  )
}

const PenaltyDateTime = ({date, dateTime, timezone}: PenaltyDateTimeProps) => {
  return (
    <Box flex="1 0 auto">
      <Text variant="labelM">{date}</Text>
      <CapitalizedText variant="bodyS" colorPath="content.neutral.secondary">
        <FormattedMessageWrapper
          defaultMessage="{time} (GMT{timezone})"
          id="cancellationPenalties.time"
          values={{
            time: (
              <FormattedTime
                value={dateTime}
                hour="2-digit"
                minute="2-digit"
                hourCycle="h23"
              />
            ),
            timezone
          }}
        />
      </CapitalizedText>
    </Box>
  )
}

const RefundLabel = ({
  refundType,
  isExpired,
  nonRefundable,
  amount
}: RefundLabelProps) => {
  if (refundType === RefundType.freeCancellation) {
    return (
      <StyledText
        variant="labelM"
        colorPath={
          isExpired ? 'content.neutral.primary' : 'content.success.secondary'
        }
        className={cx(isExpired && '--has-strike-through')}
      >
        <FormattedMessageWrapper
          id="cancellationPenalties.freeCancellation"
          defaultMessage="Free cancellation"
        />
      </StyledText>
    )
  }

  if (nonRefundable && !amount) {
    return (
      <Text variant="labelM">
        <FormattedMessageWrapper
          id="cancellationPenaltiesNoRefund"
          defaultMessage="No refund"
        />
      </Text>
    )
  }

  return (
    <StyledPriceWrapper className={cx(isExpired && '--has-strike-through')}>
      {amount}
    </StyledPriceWrapper>
  )
}

const RefundInfo = ({
  refundType,
  refundAmount,
  isExpired,
  hasChargeLaterRefundPolicy,
  isAuthenticateLater
}: RefundInfoProps) => {
  if (isExpired) {
    return (
      <Text variant="bodyS" colorPath="content.neutral.secondary">
        <FormattedMessageWrapper
          id="cancellationPenalties.expired"
          defaultMessage="Expired"
        />
      </Text>
    )
  }

  if (refundType === RefundType.freeCancellation) {
    return (
      <Text variant="bodyS" colorPath="content.success.secondary">
        {getFreeCancellationMessage(
          hasChargeLaterRefundPolicy,
          isAuthenticateLater
        )}
      </Text>
    )
  }

  if (refundAmount) {
    return (
      <Text variant="bodyS" colorPath="content.neutral.secondary">
        {refundAmount}&nbsp;
        <FormattedMessageWrapper
          id="cancellationPenalties.refund"
          defaultMessage="refund"
        />
      </Text>
    )
  }

  return null
}

export const PenaltyContainer = ({
  date,
  timezone,
  refundType,
  refundAmount,
  amount,
  id,
  nonRefundable,
  dateTime,
  hasChargeLaterRefundPolicy,
  isExpired,
  isAuthenticateLater
}: PenaltyContainerProps) => {
  return (
    <div data-id={id}>
      <Box paddingTop="s500" paddingBottom="s500">
        <Box
          display="flex"
          gap="s500"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="nowrap"
        >
          <PenaltyDateTime
            date={date}
            dateTime={dateTime}
            timezone={timezone}
          />

          <Box flex="1 1 auto" textAlign="end">
            <RefundLabel
              refundType={refundType}
              isExpired={isExpired}
              nonRefundable={nonRefundable}
              amount={amount}
            />
            <RefundInfo
              refundType={refundType}
              refundAmount={refundAmount}
              isExpired={isExpired}
              hasChargeLaterRefundPolicy={hasChargeLaterRefundPolicy}
              isAuthenticateLater={isAuthenticateLater}
            />
          </Box>
        </Box>
      </Box>
      {!nonRefundable && <Divider />}
    </div>
  )
}
